import i18next, { InitOptions, Resources } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { getAppFeatures } from '@utils/getAppFeatures'
import { initReactI18next } from 'react-i18next'
import { getTranslations, localStorageService } from '@services'
import { AppLanguages } from 'interfaces'

export interface Language {
  code: string
  nativeName: string
  apiLanguage: AppLanguages
}

export const SUPPORTED_LANGS = [
  { code: 'en-US', nativeName: 'English', apiLanguage: AppLanguages.en },
  { code: 'es-ES', nativeName: 'Español', apiLanguage: AppLanguages.es },
  { code: 'ka', nativeName: 'ქართული ენა', apiLanguage: AppLanguages.ka },
  { code: 'pl', nativeName: 'Polski', apiLanguage: AppLanguages.pl },
]

export class I18nManager {
  features = getAppFeatures()
  i18n = i18next

  default = AppLanguages.en

  loaded: AppLanguages[] = []

  loading: boolean

  languages: Language[] = SUPPORTED_LANGS?.filter((l) =>
    this.features?.i18n?.languages?.includes(l?.apiLanguage),
  )

  constructor() {
    const localStorageKey = this.features?.infra?.localStorageKey
    const options: InitOptions = {
      debug: false,
      fallbackLng: this.features?.constants?.defaultLang || this.default,
      fallbackNS: 'remote',
      defaultNS: 'remote',
      interpolation: {
        escapeValue: false, // react already safes from xss
      },
    }

    const storageSettings = localStorage?.getItem(localStorageKey)

    if (storageSettings) {
      try {
        const settings = JSON.parse(storageSettings)
        if (settings.lang) {
          options.lng = settings.lang
        }
      } catch (error) {
        // Do noting
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.i18n
      .use(initReactI18next) // passes i18n down to react-i18next
      .use(LanguageDetector)
      .init(options)
  }

  async changeLanguage(lang: AppLanguages): Promise<void> {
    const item = this.languages?.find((i) => i?.apiLanguage === lang)

    if (!item) return
    if (this.loading) return

    if (!this.loaded?.includes(item?.apiLanguage)) {
      this.loading = true
      const resources = await this.load(item)
      this.loading = false
      if (resources) this.updateResources(item?.apiLanguage, resources)
    }

    await this.i18n.changeLanguage(item?.apiLanguage).catch(() => {
      // Do noting
    })
  }

  async load(item: Language): Promise<unknown> {
    try {
      return await Promise.all([
        this.loadRemoteTranslation({
          lang: item?.apiLanguage,
        }),
        this.loadLocalTranslation({
          lang: item?.apiLanguage,
          url: this.features?.i18n?.resourcesUrls?.[item?.apiLanguage],
        }),
      ])
        .then((data) => {
          const [remote, local] = data
          return {
            ...(local ? local : {}),
            ...(remote ? remote : {}),
          }
        })
        .catch(() => {
          // Do nothing
        })
    } catch (error) {
      return null
    }
  }

  updateResources(lang: AppLanguages, resources): void {
    try {
      this.i18n.addResourceBundle(lang, 'remote', resources, true, true)

      this.i18n.setDefaultNamespace('remote')

      localStorageService?.updateLocalState({
        lang: lang,
      })
      this.loaded.push(lang)
    } catch (error) {
      // Do noting
    }
  }

  getAppLang(): AppLanguages {
    return ((localStorageService.getLocalState()?.lang as AppLanguages) ||
      this.default) as AppLanguages
  }

  async loadRemoteTranslation({
    lang,
  }: {
    lang: AppLanguages
  }): Promise<Resources | null> {
    if (this.loaded?.includes(lang)) return

    try {
      return (await getTranslations(lang))?.translations
    } catch (error) {
      // Do noting
      return null
    }
  }

  async loadLocalTranslation({
    url,
  }: {
    lang: AppLanguages
    url: string
  }): Promise<Resources | null> {
    try {
      const response = await fetch(url)
      if (response.ok) {
        return await response.json()
      }
    } catch (error) {
      // Do noting
    }
  }
}

export const i18nManager = new I18nManager()
